import { useMemo } from "react";
import { Box, Typography } from "@mui/material";

import { tokens } from "../../theme";
import GeographyChart from "../../components/GeographyChart";
import TargetMet from "../../components/targets/TargetMet";
import LoadingState from "../../components/LoadingState";

import { useAuth } from "../../AuthContext";
import PageGuard from "../../components/PageGuard";
import { useRedfidViews } from "../../utils/redfidView";

import regionsData from "../../data/regions.json";
import {
  REGION_ORDER_NORTH_TO_SOUTH,
  REGION_TO_CODE,
} from "../../utils/mapers";

const Regiones = () => {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();

  const canSeeParticipantsList = !!auth?.user?.is_admin || !!auth?.user?.is_staff;

  const regionRows = useMemo(() => {
    return Array.isArray(data?.regiones) ? data.regiones : [];
  }, [data?.regiones]);

  // -----------------------------
  // Map: id numérico -> nombre región
  // -----------------------------
  const regionNameById = useMemo(() => {
    const out = {};
    const list = Array.isArray(regionsData?.regions) ? regionsData.regions : [];

    for (const r of list) {
      const roman = String(r?.code ?? "").trim().toUpperCase();
      const name = String(r?.name ?? "").trim();
      if (!roman || !name) continue;

      if (roman === "E") {
        out["E"] = name;
        continue;
      }

      const num = REGION_TO_CODE[roman];
      if (num) out[String(num)] = name;
    }

    return out;
  }, []);

  const mapData = useMemo(() => {
    return regionRows
      .filter((r) => String(r?.id ?? "—") !== "—")
      .map((r) => ({
        id: String(r.id),
        value: Number(r.formadores_qtty ?? 0) || 0,
      }));
  }, [regionRows]);

  const OMIT_KEYS = useMemo(
    () => new Set(["-", "—", "SIN INFORMACIÓN", "SIN INFORMACION", ""]),
    []
  );

  const regionesLista = useMemo(() => {
    const REGION_RANK = Object.fromEntries(
      REGION_ORDER_NORTH_TO_SOUTH.map((id, index) => [id, index])
    );

    const sortAlphaWithOtherLast = (rows) => {
      const safe = Array.isArray(rows) ? [...rows] : [];

      return safe.sort((a, b) => {
        const aId = String(a?.id ?? "").trim();
        const bId = String(b?.id ?? "").trim();

        const aLower = aId.toLowerCase();
        const bLower = bId.toLowerCase();

        const aIsOther = aLower === "otro" || aLower === "otras carreras";
        const bIsOther = bLower === "otro" || bLower === "otras carreras";

        const aIsMissing =
          aLower === "—" ||
          aLower === "-" ||
          aLower === "sin información" ||
          aLower === "sin informacion";

        const bIsMissing =
          bLower === "—" ||
          bLower === "-" ||
          bLower === "sin información" ||
          bLower === "sin informacion";

        if (aIsMissing && !bIsMissing) return 1;
        if (!aIsMissing && bIsMissing) return -1;

        if (aIsOther && !bIsOther) return 1;
        if (!aIsOther && bIsOther) return -1;

        return aId.localeCompare(bId, "es", { sensitivity: "base" });
      });
    };

    const cleanCountRows = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((x) => {
          const id = String(x?.id ?? x?.key ?? x?.label ?? "").trim();
          const value = Number(x?.value ?? x?.count ?? 0) || 0;
          return { id, value };
        })
        .filter(({ id, value }) => {
          const ku = id.toUpperCase();
          if (OMIT_KEYS.has(ku)) return false;
          return value > 0;
        });

    const toItemStrings = (rows) => rows.map((x) => `${x.id} (${x.value})`);

    const mapped = regionRows.map((r) => {
      const rid = String(r?.id ?? "—");
      const roman = String(r?.region_roman ?? r?.region ?? "—").trim().toUpperCase();
      const idNum = Number.parseInt(rid, 10);

      const name =
        rid === "—"
          ? roman === "E"
            ? regionNameById["E"] ?? "Extranjero"
            : "Sin información"
          : regionNameById[rid] ?? `Región ${roman || rid}`;

      const universidadesClean = cleanCountRows(r?.universidades);
      const titulosClean = sortAlphaWithOtherLast(cleanCountRows(r?.titulos));
      const programasClean = sortAlphaWithOtherLast(cleanCountRows(r?.programas));

      const participantesItems = Array.isArray(r?.participantes)
        ? r.participantes
          .map((x) => String(x ?? "").trim())
          .filter((x) => x && !OMIT_KEYS.has(x.toUpperCase()))
          .sort((a, b) =>
            a.localeCompare(b, "es", { sensitivity: "base" })
          )
        : [];

      const stats = {
        participantes: Number(r?.formadores_qtty ?? 0) || 0,
        participantes_items: participantesItems,

        universidades: universidadesClean.length,
        universidades_items: toItemStrings(universidadesClean),

        carreras: titulosClean.length,
        carreras_items: toItemStrings(titulosClean),

        programas: programasClean.length,
        programas_items: toItemStrings(programasClean),
      };

      const rank =
        rid !== "—" && Number.isFinite(idNum) && REGION_RANK[idNum] != null ? REGION_RANK[idNum] : 999;

      return { id: rid, idNum, rank, name, roman, stats };
    });

    mapped.sort((a, b) => (a.rank - b.rank) || ((a.idNum ?? 999) - (b.idNum ?? 999)));
    return mapped;
  }, [regionRows, regionNameById, OMIT_KEYS]);

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  return (
    <PageGuard title="REGIONES" subtitle="Detalle por región">
      <Box m="20px" ml="5px" pb="100px">
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridAutoRows="minmax(180px, auto)"
          gap="20px"
        >
          {/* MAPA */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 3" }}
            gridRow="span 2"
            backgroundColor={colors.primary[200]}
            pb="15px"
            display="flex"
            flexDirection="column"
            sx={{ justifySelf: "start", width: "400px", height: "1617px" }}
          >
            <Typography
              variant="h4"
              fontWeight="600"
              sx={{ marginBottom: "15px" }}
              color={colors.primary[100]}
            >
              DISTRIBUCIÓN GEOGRÁFICA DE PARTICIPANTES
            </Typography>

            <Box flex="1" minHeight="370px">
              <GeographyChart data={mapData} />
            </Box>
          </Box>

          {/* DETALLE */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 9" }}
            gridRow="span 2"
            backgroundColor={colors.primary[200]}
            display="flex"
            flexDirection="column"
            sx={{ minWidth: 0 }}
          >
            <Box pb="15px">
              <Typography variant="h4" fontWeight="600" color={colors.primary[100]}>
                REGIONES
              </Typography>
            </Box>

            <Box sx={{ px: 0, pb: 6, pr: 0, pt: 0 }} display="flex" flexDirection="column" gap="14px">
              {regionesLista.map((r) => (
                <TargetMet
                  key={`${r.id}-${r.roman}`}
                  title={r.name}
                  variant="dash"
                  fullWidth
                  bgColor={colors.primary[200]}
                  sx={{ borderRadius: "18px" }}
                  titleWrap
                  stats={[
                    {
                      key: "participantes",
                      label: {
                        singular: "Participante",
                        plural: "Participantes",
                      },
                      value: r.stats.participantes,
                      items: r.stats.participantes_items,
                    },
                    {
                      key: "universidades",
                      label: {
                        singular: "Universidad",
                        plural: "Universidades",
                      },
                      value: r.stats.universidades,
                      items: r.stats.universidades_items,
                    },
                    {
                      key: "título profesional",
                      label: {
                        singular: "Título profesional",
                        plural: "Títulos profesionales",
                      },
                      value: r.stats.carreras,
                      items: r.stats.carreras_items,
                    },
                    {
                      key: "programas",
                      label: {
                        singular: "Programa",
                        plural: "Programas",
                      },
                      value: r.stats.programas,
                      items: r.stats.programas_items,
                    },
                  ]}
                  columns={2}

                  disableParticipantsDetail={!canSeeParticipantsList}
                  participantsStatKey="participantes"

                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageGuard>
  );
};

export default Regiones;
